/** UI-17: pure calendar arithmetic for the scheduling picker. Months are 0-based like Date. */
export interface YearMonth {
  readonly year: number;
  readonly month: number;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** Weeks (Monday first) of the month; cells outside the month are null; every week has 7 cells. */
export function monthGrid(year: number, month: number): (Date | null)[][] {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7;
  const cells: (Date | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];
  const padded = [...cells, ...Array.from({ length: (7 - (cells.length % 7)) % 7 }, () => null)];
  return Array.from({ length: padded.length / 7 }, (_, w) => padded.slice(w * 7, w * 7 + 7));
}

export function shiftMonth(ym: YearMonth, by: number): YearMonth {
  const d = new Date(ym.year, ym.month + by, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

/** A day plus an `HH:mm` time (09:00 when blank) as a `datetime-local` value. */
export function combineDateTime(day: Date, time: string): string {
  const t = /^\d{2}:\d{2}$/.test(time) ? time : '09:00';
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}T${t}`;
}

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** RPT-11 (revised) / UI-60: an inclusive "To" day asks the API (exclusive upper bound) for the day after. */
export function dayAfter(day: string): string {
  const t = new Date(`${day}T00:00:00Z`).getTime();
  return Number.isNaN(t) ? day : new Date(t + 86_400_000).toISOString().slice(0, 10);
}
